import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TherapistSpace, TherapistSpaceInput } from "../../domain/types";

interface SpaceFormModalProps {
    open: boolean;
    space?: TherapistSpace | null;
    onClose: () => void;
    onSubmit: (payload: TherapistSpaceInput) => void;
    isSaving?: boolean;
    errorMessage?: string | null;
}

export default function SpaceFormModal({
    open,
    space,
    onClose,
    onSubmit,
    isSaving = false,
    errorMessage = null,
}: SpaceFormModalProps) {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [notes, setNotes] = useState("");
    const [isPublic, setIsPublic] = useState(false);

    useEffect(() => {
        setName(space?.name ?? "");
        setAddress(space?.address ?? "");
        setNotes(space?.notes ?? "");
        // Off for a new space. One of a therapist's spaces is frequently their
        // home, so publication is never something they get by not noticing.
        setIsPublic(space?.is_public ?? false);
    }, [space, open]);

    if (!open) return null;

    const nameIsBlank = !name.trim();

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={
                space
                    ? t("scheduler.settings.editSpaceTitle")
                    : t("scheduler.settings.newSpaceTitle")
            }
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (nameIsBlank) return;
                    onSubmit({
                        name: name.trim(),
                        address: address.trim() || null,
                        notes: notes.trim() || null,
                        is_public: isPublic,
                    });
                }}
                className="flex w-full max-w-md flex-col gap-3 rounded-2xl bg-white p-6 dark:bg-gray-900"
            >
                <h3 className="text-lg font-semibold">
                    {space
                        ? t("scheduler.settings.editSpaceTitle")
                        : t("scheduler.settings.newSpaceTitle")}
                </h3>

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.settings.spaceName")}
                    <input
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        aria-label={t("scheduler.settings.spaceName")}
                        required
                        className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.settings.spaceAddress")}
                    <input
                        value={address}
                        onChange={(event) => setAddress(event.target.value)}
                        aria-label={t("scheduler.settings.spaceAddress")}
                        className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.settings.spaceNotes")}
                    <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        aria-label={t("scheduler.settings.spaceNotes")}
                        className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">
                    <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(event) => setIsPublic(event.target.checked)}
                        aria-label={t("scheduler.settings.spaceIsPublic")}
                        className="mt-0.5"
                    />
                    <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-content-primary">
                            {t("scheduler.settings.spaceIsPublic")}
                        </span>
                        {/* Said plainly: this puts a street address on a page
                            anyone can read. */}
                        <span className="text-xs text-content-tertiary">
                            {t("scheduler.settings.spaceIsPublicHint")}
                        </span>
                    </span>
                </label>

                {errorMessage && (
                    <p role="alert" className="text-sm text-red-600">
                        {errorMessage}
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm">
                        {t("scheduler.common.cancel")}
                    </button>
                    <button
                        type="submit"
                        disabled={nameIsBlank || isSaving}
                        className="rounded-lg bg-brand-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                    >
                        {isSaving
                            ? t("scheduler.settings.saving")
                            : t("scheduler.common.save")}
                    </button>
                </div>
            </form>
        </div>
    );
}
